import { z } from "zod";

import { calculationRunSummarySchema } from "./calculationRun";
import { jurisdictionProfileSchema } from "./cableContract";
import { exactDecimalSchema } from "./faultContract";
import { ApiError, describeApiFieldError } from "./http";
import { loadScenarioSchema } from "./loadDemandContract";
import {
  REQUIRED_UPS_CHOICES,
  upsBatteryTechnologySchema,
  upsEnergyBasisSchema,
  upsGoverningBasisSchema,
  upsPhaseConfigurationSchema,
  upsRedundancyModeSchema,
  upsSizingStatusSchema,
  upsSizingWarningCodeSchema,
  upsTopologySchema,
} from "./upsSizingContract";

const optionalRequestText = (maxLength: number) =>
  z.string().trim().max(maxLength).nullable().optional();

// A factor left out means NOT ESTABLISHED, never 1: the engine sizes with 1,
// names the factor in a warning and reports REVIEW_REQUIRED (Master Prompt
// A16 (a), A18 (f)). The form must omit a blank field instead of sending "1".
const optionalFactor = exactDecimalSchema.optional();

/** The same sentence the Transformer and DG rating schedules use. */
const RATINGS_ASCENDING = "Ratings must be unique and in ascending order.";

export const upsSizingRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    critical_load_kw: exactDecimalSchema,
    // Engineering inputs, not margins: no default, required.
    load_power_factor: exactDecimalSchema,
    ups_rated_output_power_factor: exactDecimalSchema,
    // A design-basis input, never defaulted (A16 (b)).
    required_runtime_minutes: exactDecimalSchema,
    available_unit_ratings_kva: z.array(exactDecimalSchema).min(1),
    inverter_efficiency: optionalFactor,
    future_growth_factor: optionalFactor,
    design_margin_factor: optionalFactor,
    ambient_derating_factor: optionalFactor,
    altitude_derating_factor: optionalFactor,
    duty_modules: z.number().int().positive().optional(),
    redundant_modules: z.number().int().nonnegative().optional(),
    // The engineer's choice; the backend imposes no default (A18 (h)). Optional here
    // only so a missing choice is reported by the rule below as a missing value.
    redundancy_mode: upsRedundancyModeSchema.optional(),
    // Descriptors: optional, no default, echoed only (A18 (h)).
    topology: upsTopologySchema.optional(),
    phase_configuration: upsPhaseConfigurationSchema.optional(),
    battery_technology: upsBatteryTechnologySchema.optional(),
    scenario: loadScenarioSchema.optional(),
    jurisdiction_profile: jurisdictionProfileSchema.optional(),
    notes: optionalRequestText(1000),
    // The UPS source of the revision's diagram this study sizes (A28 (d)); left out when
    // none is chosen, so a study that does not use the link is sent as before.
    sld_element_code: z.string().trim().min(1).max(50).optional(),
  })
  .strict()
  .superRefine((values, ctx) => {
    // Mirrors the backend rule (schemas/ups_sizing.py): the schedule must be
    // unique and ascending. Only the first offending entry is flagged, so the
    // message points at one place on screen; an entry that is blank or not a
    // plain decimal already carries its own issue.
    let previous: number | null = null;
    for (const [index, raw] of values.available_unit_ratings_kva.entries()) {
      if (!exactDecimalSchema.safeParse(raw).success) {
        continue;
      }
      const current = Number(raw);
      if (previous !== null && !(current > previous)) {
        ctx.addIssue({
          code: "custom",
          message: RATINGS_ASCENDING,
          path: ["available_unit_ratings_kva", index],
        });
        break;
      }
      previous = current;
    }

    for (const field of REQUIRED_UPS_CHOICES) {
      if (values[field] === undefined) {
        // Reported as a missing value rather than an unknown option, so the form's
        // describeValidationIssue turns it into "... is required." - the same way
        // the DG form reports a missing choice.
        ctx.addIssue({
          code: "invalid_type",
          expected: "string",
          received: "undefined",
          message: `${field} is required`,
          path: [field],
        });
      }
    }
  });

export const upsSizingWarningSchema = z
  .object({
    code: upsSizingWarningCodeSchema,
    message: z.string(),
  })
  .strict();

export const upsSizingResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    // Descriptors come back as given, null when not declared.
    scenario: loadScenarioSchema.nullable(),
    topology: upsTopologySchema.nullable(),
    phase_configuration: upsPhaseConfigurationSchema.nullable(),
    battery_technology: upsBatteryTechnologySchema.nullable(),
    redundancy_mode: upsRedundancyModeSchema,
    critical_load_kw: z.string(),
    load_power_factor: z.string(),
    ups_rated_output_power_factor: z.string(),
    // The factor the sizing used; "1" when it was not established.
    future_growth_factor: z.string(),
    design_margin_factor: z.string(),
    design_load_kw: z.string(),
    kva_requirement_kva: z.string(),
    active_power_requirement_kva: z.string(),
    governing_basis: upsGoverningBasisSchema,
    governing_requirement_kva: z.string(),
    combined_derating_factor: z.string(),
    derated_required_capacity_kva: z.string(),
    duty_modules: z.number().int(),
    redundant_modules: z.number().int(),
    total_modules: z.number().int(),
    // The schedule the selection was made from, echoed in the order it was sent.
    available_unit_ratings_kva: z.array(exactDecimalSchema).min(1),
    required_unit_rating_kva: z.string(),
    // Null together with the six below when no rating was adequate (NO_SOLUTION).
    selected_unit_rating_kva: z.string().nullable(),
    installed_nameplate_capacity_kva: z.string().nullable(),
    derated_duty_capacity_kva: z.string().nullable(),
    derated_duty_capacity_kw: z.string().nullable(),
    spare_derated_capacity_kva: z.string().nullable(),
    loading_kva_percent: z.string().nullable(),
    loading_kw_percent: z.string().nullable(),
    required_runtime_minutes: z.string(),
    // The efficiency the energy figure used; "1" when it was not established.
    inverter_efficiency: z.string(),
    energy_basis: upsEnergyBasisSchema,
    load_energy_kwh: z.string(),
    battery_dc_energy_kwh: z.string(),
    status: upsSizingStatusSchema,
    warnings: z.array(upsSizingWarningSchema),
    jurisdiction_profile: jurisdictionProfileSchema,
  })
  .strict();

export const upsRunCreateRequestSchema = z
  .object({
    study: upsSizingRequestSchema,
    project_revision_id: z.string().uuid().optional(),
    notes: optionalRequestText(2000),
  })
  .strict();

export const upsRunResponseSchema = z
  .object({
    run: calculationRunSummarySchema,
    result: upsSizingResponseSchema,
  })
  .strict();

export type UPSSizingRequest = z.infer<typeof upsSizingRequestSchema>;
export type UPSSizingWarning = z.infer<typeof upsSizingWarningSchema>;
export type UPSSizingResponse = z.infer<typeof upsSizingResponseSchema>;
export type UPSRunCreateRequest = z.infer<typeof upsRunCreateRequestSchema>;
export type UPSRunResponse = z.infer<typeof upsRunResponseSchema>;

function formatApiError(data: unknown, status: number): string {
  return describeApiFieldError(data, status, "UPS sizing failed");
}

/**
 * Calculate a UPS sizing study and persist it as a run revision.
 *
 * The request carries the whole run body, because a study's notes belong to the
 * run, not to the sizing the engine calculates. The returned summary carries the
 * run ID, engine version, profile, reference status and content hash shown in
 * the traceability panel.
 *
 * With a project revision id the run is stored under that revision of the selected
 * project; without one it belongs to no project. An unchanged study returns the
 * stored revision with HTTP 200 instead of a new one (A11); both are valid here.
 */
export async function createUPSRun(
  payload: UPSRunCreateRequest,
  signal?: AbortSignal,
  projectRevisionId?: string,
): Promise<UPSRunResponse> {
  const validatedPayload = upsRunCreateRequestSchema.parse(payload);
  const timeoutSignal = AbortSignal.timeout(30_000);
  const requestSignal = signal
    ? AbortSignal.any([signal, timeoutSignal])
    : timeoutSignal;

  const response = await fetch("/api/v1/electrical/ups-sizing/runs", {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(
      projectRevisionId === undefined
        ? validatedPayload
        : { ...validatedPayload, project_revision_id: projectRevisionId },
    ),
    cache: "no-store",
    signal: requestSignal,
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(formatApiError(data, response.status), response.status);
  }

  const parsed = upsRunResponseSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error("Unexpected response from the KES Electrical OS UPS runs API.");
  }

  return parsed.data;
}
